import { Container, Row, Col, CardBody, Card, Table, Badge } from 'reactstrap';
import React, { Fragment, useState, useEffect, useContext, useMemo } from 'react';
import { H5 } from '../../../AbstractElements';
import CountUp from 'react-countup';
import { Link } from 'react-router-dom';
import { ApiRequestBuilderV2, TilesBuilder } from '../../../GlobalComponents/Builders';
import { AccountContext } from '../../../GlobalComponents/Authentication/Accounts';
import { AlertCircle, CheckCircle, ExternalLink } from 'react-feather';


const Dashboard = () => {

    const [numberOfPartners, setNumberOfPartners] = useState(0);
    const [numberOfActiveTools, setNumberOfActiveTools] = useState(0);
    const [numberOfCustomTools, setNumberOfCustomTools] = useState(0);
    const [numberOfActiveProjects, setNumberOfActiveProjects] = useState(0);
    const [syncErrorsCount, setSyncErrorsCount] = useState(0);
    const [monitoredJobs, setMonitoredJobs] = useState([]);

    const { userSession } = useContext(AccountContext)

    useEffect(() => {

        document.title = 'View Dashboard'

        // Fetch all dashboard tile data in a single call
        ApiRequestBuilderV2('dashboardAdminGet')
            .then(function (res) {
                if (res.success && res.data) {
                    setNumberOfPartners(res.data.partners_count || 0)
                    setNumberOfActiveTools(res.data.active_tools_count || 0)
                    setNumberOfCustomTools(res.data.custom_tools_count || 0)
                    setNumberOfActiveProjects(res.data.active_projects_count || 0)
                    setSyncErrorsCount(res.data.sync_errors_count || 0)
                }
            })

        // Fetch monitored DMT jobs for dashboard
        ApiRequestBuilderV2('dmtDashboardGet')
            .then(function (res) {
                if (res.success && res.data) {
                    setMonitoredJobs(res.data)
                }
            })

    }, []);

    // Group jobs by partner for row grouping display
    const jobsByPartner = useMemo(() => {
        const grouped = {}
        monitoredJobs.forEach(job => {
            const partner = job.partner_name || 'Unknown'
            if (!grouped[partner]) grouped[partner] = []
            grouped[partner].push(job)
        })
        return grouped
    }, [monitoredJobs])

    var tiles = [
        {
            id: 1,
            icon: 'Users',
            title: 'Partners',
            count: <CountUp end={numberOfPartners} duration={3} />,
            backgroundIcon: 'Users'
        },
        {
            id: 2,
            icon: 'ShoppingBag',
            title: 'Active Tools',
            count: <CountUp end={numberOfActiveTools} duration={2} />,
        },
        {
            id: 3,
            icon: 'ShoppingBag',
            title: 'Custom Tools',
            count: <CountUp end={numberOfCustomTools} duration={2} />,
        },
        {
            id: 4,
            icon: 'MessageCircle',
            title: 'Active Projects',
            count: <CountUp end={numberOfActiveProjects} duration={2} />,
        }
    ]

    return (
        <Fragment>
            <Container fluid={true}>
                <TilesBuilder data={tiles} />

                {/* DMT Monitored Jobs Section */}
                {monitoredJobs.length > 0 && (
                    <Row className="mt-4">
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <H5 attrH5={{ className: 'mb-0' }}>Monitored DMT Jobs</H5>
                                        <Link
                                            to={`${process.env.PUBLIC_URL}/data-migration/sync-errors`}
                                            className={`d-inline-flex align-items-center ${syncErrorsCount > 0 ? 'txt-danger fw-bold' : 'txt-primary'}`}
                                            style={{ gap: 4, fontSize: 13 }}
                                        >
                                            <AlertCircle size={16} /> Sync Errors{syncErrorsCount > 0 ? ` (${syncErrorsCount})` : ''}
                                        </Link>
                                    </div>
                                    <Table hover responsive className="table-border-horizontal">
                                        <thead>
                                            <tr>
                                                <th>Job</th>
                                                <th>Type</th>
                                                <th>Source → Destination</th>
                                                <th className="text-center">Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(jobsByPartner).map(([partnerName, jobs]) => (
                                                <Fragment key={partnerName}>
                                                    {/* Partner group header row */}
                                                    <tr className="table-group-header">
                                                        <td colSpan="5" className="fw-bold py-2">
                                                            {partnerName}
                                                        </td>
                                                    </tr>
                                                    {/* Job rows for this partner */}
                                                    {jobs.map((job) => (
                                                        <tr key={job.job_id}>
                                                            <td className="ps-4">{job.job_name}</td>
                                                            <td>
                                                                <Badge color={job.job_type === 'migration' ? 'secondary' : 'primary'}>
                                                                    {job.source_service_label?.toUpperCase()} → {job.destination_service_label?.toUpperCase()} {job.job_type === 'migration' ? 'Migration' : 'Sync'}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-muted f-12">
                                                                {job.source_connection} → {job.destination_connection}
                                                            </td>
                                                            <td className="text-center">
                                                                {job.job_type === 'sync' ? (
                                                                    (job.error_count > 0 || job.failed_count > 0) ? (
                                                                        <span className="txt-danger">
                                                                            <AlertCircle size={16} className="me-1" />
                                                                            {job.error_count > 0 && <span className="me-2">{job.error_count} error{job.error_count !== 1 ? 's' : ''}</span>}
                                                                            {job.failed_count > 0 && <span>{job.failed_count} failed</span>}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="txt-success">
                                                                            <CheckCircle size={16} className="me-1" />
                                                                            Healthy
                                                                        </span>
                                                                    )
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Link 
                                                                    to={`${process.env.PUBLIC_URL}/tools/data-migration/jobs/${job.job_id}${job.job_type === 'sync' ? '/progress' : ''}`}
                                                                    className="txt-primary"
                                                                >
                                                                    <ExternalLink size={16} />
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </Fragment>
                                            ))}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </Fragment>
    );
};

export default Dashboard;